import { Card, Checkbox, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useSelector } from 'react-redux';
import { CardBgcolor, PageHeader, TableHeader } from '../../app/AppStyles';
import { GetDate, GetTime, SearchKeyFilter } from '../../app/AppUtils';
import SearchField from './SearchField';
import TablePaginationActions from './TablePaginationActions';

export default function ContentSelector({
    content,
    contentId,
    contentTitle,
    contentHeaders,
    contentRows,
    selectedContent,
    setSelectedContent,
    multiple,
    defaultRowsPerPage
}) {
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);

    const [filter, setFilter] = useState("");

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, contentRows, filter);
    }

    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage ?? 5);
    const [page, setPage] = useState(0);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function EnhancedTableHead({ onSelectAllClick, numSelected, rowCount }) {
        return (
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                        {multiple && (
                            <Checkbox
                                color="primary"
                                indeterminate={numSelected > 0 && (filter ? numSelected < content?.filter(handleFilterResult)?.length : numSelected < rowCount)}
                                checked={rowCount > 0 && (filter ? numSelected === content?.filter(handleFilterResult)?.length : numSelected === rowCount)}
                                onChange={onSelectAllClick}
                                inputProps={{ 'aria-label': 'select all' }}
                            />
                        )}
                    </TableCell>
                    {contentHeaders?.map((item, index) =>
                        <TableCell key={index} align="center" sx={{ ...TableHeader }}>{item}</TableCell>
                    )}
                </TableRow>
            </TableHead>
        );
    };

    const handleSelectAllClick = (event) => {
        if (!multiple) return;

        if (event.target.checked) {
            const newSelected = content?.filter(handleFilterResult)?.map((n) => n);
            setSelectedContent(newSelected);
        } else {
            setSelectedContent([]);
        }
    };

    const handleClick = (event, item) => {
        const id = item[`${contentId}`];

        if (!multiple) {
            const isAlreadySelected = selectedContent?.[0]?.[`${contentId}`] === id;
            setSelectedContent(isAlreadySelected ? [] : [item]);
        } else {
            const selectedIndex = selectedContent?.findIndex(x => x[`${contentId}`] === id);
            let newSelected = [];

            if (selectedIndex === -1) {
                newSelected = selectedContent.concat(item);
            } else {
                newSelected = selectedContent.filter(x => x[`${contentId}`] !== id);
            }

            setSelectedContent(newSelected);
        }
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - content?.filter(handleFilterResult)?.length) : 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const isSelected = (id) => selectedContent?.findIndex(x => x[`${contentId}`] === id) !== -1 // selectedContent needs to be an empty array default

    return (
        <Card elevation={0} sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2, bgcolor: CardBgcolor }}>
            <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                <Stack spacing={1} sx={{ flex: '1 1 auto', maxWidth: 1, p: 0.5, display: 'flex', justifyItems: 'center', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ ...PageHeader }}>{contentTitle}</Typography>
                    <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                    <TableContainer>
                        <Table>
                            <EnhancedTableHead numSelected={selectedContent?.length} onSelectAllClick={handleSelectAllClick} rowCount={selectedContent?.length} />
                            <TableBody>
                                {(rowsPerPage > 0 ? content?.filter(handleFilterResult)?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : content?.filter(handleFilterResult))?.map((item, index) => {
                                    const isItemSelected = isSelected(item[`${contentId}`]);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow hover onClick={(event) => handleClick(event, item)} role="checkbox" aria-checked={isItemSelected} tabIndex={-1} key={index} selected={isItemSelected} sx={{ cursor: 'pointer' }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isItemSelected}
                                                    inputProps={{ 'aria-labelledby': labelId, }}
                                                />
                                            </TableCell>
                                            {contentRows?.map((displayItem, index) =>
                                                displayItem.toLowerCase().includes("start") || displayItem.toLowerCase().includes("date") ? (
                                                    <TableCell key={index} align="center">{item[`${displayItem}`] ? `${GetDate(item[`${displayItem}`])} (${GetTime(item[`${displayItem}`])})` : "-"}</TableCell>
                                                ) : (
                                                    <TableCell key={index} align="center">{item[`${displayItem}`]}</TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    );
                                })}
                                {(rowsPerPage > 0 ? content?.filter(handleFilterResult)?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : content?.filter(handleFilterResult))?.length === 0 && <TableRow><TableCell colSpan={4}>{resourceFinal[appLanguage].messages.RESULT_EMPTY}</TableCell></TableRow>}
                                {emptyRows > 0 && <TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={4} /></TableRow>}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        sx={{ border: 0 }}
                                        rowsPerPageOptions={[5, 10, 25, 50,
                                            {
                                                label: content?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                                value: content?.length < 100 ? -1 : 100
                                            }
                                        ]}
                                        count={content ? content?.filter(handleFilterResult)?.length : 0}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        ActionsComponent={TablePaginationActions}
                                        labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                                        labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Stack>
            </Scrollbars>
        </Card>
    );
}
